import * as React from 'react';
import classNames from 'classnames';
import { CSSTransition } from 'react-transition-group';
import Button from 'PyzShopUi/scripts/react/common/components/form/Button';
import { ACCORDION_SCROLLABLE_PARENT_CLASS_NAME } from 'PyzShopUi/scripts/shop-ui/constants';
import MdcLanguageSelect
    from 'PyzShopUi/scripts/product-review/components/product-review-list/common/MdcLanguageSelect';
import { useTranslationHook } from 'PyzShopUi/scripts/utils/translationHook';
import SortSelect from 'PyzShopUi/scripts/shop-ui/components/search-page/components/sidebar/SortSelect';
import {
    IPagination,
    ISelectFilter,
    ISort
} from 'PyzShopUi/scripts/shop-ui/components/search-page/interfaces';
import {ISelectOption} from 'PyzShopUi/scripts/shop-ui/interfaces';
import {DEFAULT_PARAM_LANGUAGE, PARAM_LANGUAGE} from 'PyzShopUi/scripts/product-review/constants';
import StarItem from 'PyzShopUi/scripts/product-review/components/total-review-rating/StarItem';
import {IStarItem} from 'PyzShopUi/scripts/product-review/interfaces';
import Accordion from 'PyzShopUi/scripts/shop-ui/components/Accordion';
import Loading from 'PyzShopUi/scripts/react/common/components/Loading';

interface ISidebarProps {
    pagination: IPagination;
    sort?: ISort;
    filters?:ISelectFilter[]
    isProgressing: boolean;
    dispatch: React.Dispatch<any>;
    ratingStars: IStarItem[];
}

const SortFilterSidebar: React.FunctionComponent<ISidebarProps> = props => {
    const {
        pagination,
        sort,
        isProgressing,
        dispatch,
        filters,
        ratingStars
    } = props;

    const [sidebarActive, setSidebarActive] = React.useState<boolean>(false);
    const { translate } = useTranslationHook();
    const trapFocusRef = React.useRef<HTMLDivElement>();
    const buttonRef = React.useRef<HTMLButtonElement>();


    const pageNumFound: number = pagination?.numFound || 0;
    const sortParamNames: string[] = sort?.sortParamNames;
    const currentSortParam: string = sort?.currentSortParam;

    const filtersShowResultsText = translate('showReview', { ns: 'article-detail', count: pageNumFound });

    const activityGroupClassNames = classNames(
        'sidebar__activity-group',
        {
            [ACCORDION_SCROLLABLE_PARENT_CLASS_NAME]: sidebarActive,
        },
    );

    const wrapTransition = (element: React.ReactNode) => {
        return <CSSTransition appear={true} in={sidebarActive} classNames="sidebar__activity-group--active" timeout={100} >
            {element}
        </CSSTransition>
    }

    const onToggleSidebar = () => setSidebarActive(!sidebarActive);
    const getLanguageOptionFromFilter = () => {
        return filters?.find(option => option.name === PARAM_LANGUAGE)?.options || []
    }
    const getCurrentLanguageOptionFromFilter = () => {
        const options = getLanguageOptionFromFilter();
        return options.find(option => option.selected)?.value || DEFAULT_PARAM_LANGUAGE;
    }
    const itemLanguageOptions: ISelectOption[] = getLanguageOptionFromFilter()
    const currentItemsLanguage: string = getCurrentLanguageOptionFromFilter();

    React.useEffect(() => {
        if(sidebarActive) {
            trapFocusRef.current?.focus();
        }
    }, [sidebarActive])


    const displaySelectLanguageAndSort = () => {
        return (
            <div className="sorter-section show-for-small-only">
                <span>
                    {translate('Filter & Sorting', { ns: 'article-list' })}
                </span>

                <div className="grid-x grid-margin-x">
                    <div className="cell small-5">
                        <MdcLanguageSelect
                            itemLanguageOptions={itemLanguageOptions}
                            currentItemsLanguage={currentItemsLanguage}
                            dispatch={dispatch}
                        />
                    </div>
                    <div className="cell small-7">
                        <SortSelect
                            sortParamNames={sortParamNames}
                            currentSortParam={currentSortParam}
                            dispatch={dispatch}
                        />
                    </div>
                </div>
            </div>
        );
    };
    const sortedStars = ratingStars.sort((a, b) => b.averageRating - a.averageRating);
    const displayRatingStars = () => sortedStars.map((starItem: IStarItem) => {
        const onClickRating = () => {}

        return (
            <StarItem
                key={starItem.averageRating}
                averageRating={starItem.averageRating}
                reviewCount={starItem.reviewCount.count}
                isActive={starItem.reviewCount.isActive}
                isLinkShownFull={false}
                onClick={onClickRating}
                dispatch={dispatch}
            />
        );
    });

    const displayAccordion = (el, label, textDomain) => {
        return (
            <Accordion
                label={translate(label, { ns: textDomain })}
                hideHeaderContentOnCollapsed={true}
                isScrollToCurrentOnCollapsed={true}
            >
                {el}
            </Accordion>
        );
    };

    return (
        <React.Suspense fallback={<Loading />}>
            <div className="sidebar filter-and-sort-for-mobile">
                <Button
                    label={translate('Filter & Sorting', { ns: 'article-list' })}
                    icon="filter"
                    additionalClass="show-for-small-only button button-filter button-bordered button-leading-icon button-open-filter button-grey"
                    onClick={onToggleSidebar}
                    dataQa="trigger-open-sorting-and-filters"
                />

                {wrapTransition(
                        <div className={activityGroupClassNames}>
                            <div 
                                ref={trapFocusRef} 
                                className='show-for-small-only'
                                role="button"
                                aria-label="Focus Trap" 
                                tabIndex={0} 
                                style={{ outline: "none" }} 
                                onKeyDown={e => {
                                    if (e.key === "Tab" && e.shiftKey) {
                                        e.preventDefault();
                                        buttonRef?.current?.focus();
                                    }
                                }}
                                >
                            </div>

                            <div className="sidebar__activity-group__mobile-label show-for-small-only">
                                <span> {translate('Filter & Sorting', { ns: 'article-list' })} </span>
                            </div>
        
                            {displaySelectLanguageAndSort()}
                            <div className={`filter-list`}>
                                {displayAccordion(displayRatingStars(), 'Review', 'article-detail')}
                            </div>
        
                            <div className="button-show-list show-for-small-only">
                                <Button
                                    buttonRef={buttonRef}
                                    label={filtersShowResultsText}
                                    icon="arrow"
                                    iconAdditionalClass="icon-size-small icon-arrow-filter"
                                    additionalClass="button button-progressing button-red"
                                    onClick={onToggleSidebar}
                                    isProgressing={isProgressing}
                                    iconPosition="right"
                                    dataQa="trigger-close-sorting-and-filters"
                                    onKeyDown={e => {
                                        if (e.key === "Tab" && !e.shiftKey) {
                                            e.preventDefault();
                                            trapFocusRef?.current?.focus();
                                        }
                                    }}
            
                                />
                            </div>
                        </div>
                    )} 
            </div>
        </React.Suspense>
    );
};

export default SortFilterSidebar;
